<template>
  <SPAModal
    :id="'CreateProjectModal'"
    staticBackdrop
    v-model="createModalState"
    @hide="openConfirmationModal"
  >
    <template #modal-title>
      <div class="modal-title-container">
        <label>{{ titleLabel }}</label>
        {{ titleDescription }}
      </div>
    </template>
    <template #modal-body>
      <div class="modal-input-container">
        <div class="modal-input">
          <MDBInput
            v-model.trim="projectCode"
            :wrapperClass="addErrClass('projectCode')"
            @blur="handleError('projectCode')"
            @keyup="handleKeyup"
            label="Project Code"
            :maxlength="20"
            autocomplete="off"
            invalidFeedback="Required"
            :required="codeErr.onBlurState"
            counter
            :helper="codeErr.errMsg"
            :readonly="editModalState"
          />
          <MDBInput
            v-model.trim="projectName"
            :wrapperClass="addErrClass('projectName')"
            @blur="handleError('projectName')"
            @keyup="handleKeyup"
            label="Project Name"
            :maxlength="100"
            autocomplete="off"
            invalidFeedback="Required"
            :required="nameErr.onBlurState"
            counter
            :helper="nameErr.errMsg"
          />
        </div>
        <div>
          <SPASelect
            v-model:options="projectStatusOptions"
            v-model:selected="projectStatus"
            label="Status"
            :setValue="1"
            :setOption="1"
          />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <MDBBtn
        color="light"
        class="text-capitalize"
        @click="openConfirmationModal"
        >Cancel</MDBBtn
      >
      <MDBBtn
        color="success"
        :class="[disabledClass, 'text-capitalize']"
        @click="handleSubmit"
        >{{ submitBtnText }}</MDBBtn
      >
    </template>
  </SPAModal>
  <SPAModal
    :id="'ConfirmProjectModal'"
    staticBackdrop
    v-model="confirmModalState"
    @hide="closeConfirmationModal"
    variant="warning"
  >
    <template #modal-title>
      <div class="modal-title-container">
        <label>Unsaved Changes</label>
      </div>
    </template>
    <template #modal-body>
      <div class="modal-input-container">
        Are you sure you want to discard the changes you made?
      </div>
    </template>
    <template #modal-footer>
      <MDBBtn
        color="light"
        class="text-capitalize"
        @click="closeConfirmationModal"
        >Back</MDBBtn
      >
      <MDBBtn
        color="warning"
        class="text-capitalize"
        @click="discardCreateModal"
        >Yes, discard</MDBBtn
      >
    </template>
  </SPAModal>
</template>

<script>
import SPAModal from '../../../components/SPAModal.vue';
import { MDBInput, MDBBtn } from 'mdb-vue-ui-kit';
import { useProjectTaggingStore } from '../../../stores/projectTagging';
import { storeToRefs } from 'pinia';
import { PostProjects } from '../../../services/ProjectTagging/PostProjects';
import { PatchProjects } from '../../../services/ProjectTagging/PatchProjects';
import {
  isProjectCodeEmpty,
  isProjectNameEmpty,
} from '../Business/ProjectTaggingBusiness';
import { computed, ref, watch } from 'vue';
import SPASelect from '../../../components/SPASelect.vue';
export default {
  components: {
    SPAModal,
    MDBInput,
    MDBBtn,
    SPASelect,
  },
  setup() {
    const projectTagging = useProjectTaggingStore();
    const {
      projectCode,
      projectName,
      projectStatus,
      toastState,
      toastProjectName,
      createModalState,
      editModalState,
      toastEditState,
      projectId,
      projectStatusOptions,
      projectNameDescription,
    } = storeToRefs(projectTagging);
    const isCreateBtnDisabled = computed(
      () =>
        isProjectCodeEmpty(projectCode.value) ||
        isProjectNameEmpty(projectName.value),
    );
    const disabledClass = computed(() => [
      { disabled: isCreateBtnDisabled.value },
    ]);
    const submitBtnText = computed(() =>
      editModalState.value ? 'Confirm Details' : 'Create Project',
    );
    const titleLabel = computed(() =>
      editModalState.value ? 'Edit Project' : 'Create Project',
    );
    const titleDescription = computed(() =>
      editModalState.value
        ? `Edit the details needed for ${projectNameDescription.value}:`
        : 'Please provide the following details when creating a project:',
    );
    const isInputEdited = ref(false);
    const handleKeyup = () => {
      isInputEdited.value = true;
    };
    const confirmModalState = ref(false);
    const isDiscard = ref(false);
    const resetCreateProjectModal = () => {
      projectCode.value = '';
      projectName.value = '';
      projectStatusOptions.value = resetStatusOptions();
      codeErr.value.onBlurState = false;
      nameErr.value.onBlurState = false;
      codeErr.value.errMsg = '';
      nameErr.value.errMsg = '';
      editModalState.value = false;
      isInputEdited.value = false;
    };
    const mapStatusOptions = (compareItem) => {
      return projectStatusOptions.value.map((item) => {
        return {
          ...item,
          selected: item.value == compareItem,
        };
      });
    };
    const resetStatusOptions = () => {
      return mapStatusOptions(1);
    };
    watch(editModalState, (newEditModalState) => {
      if (newEditModalState) {
        projectStatusOptions.value = mapStatusOptions(projectStatus.value);
      }
    });
    const discardCreateModal = () => {
      isDiscard.value = true;
      resetCreateProjectModal();
      confirmModalState.value = false;
      createModalState.value = false;
    };
    const closeConfirmationModal = () => {
      if (!isDiscard.value) {
        createModalState.value = true;
      }
      confirmModalState.value = false;
    };
    const openConfirmationModal = () => {
      isDiscard.value = false;
      createModalState.value = false;
      if (
        (isProjectCodeEmpty(projectCode.value) &&
          isProjectNameEmpty(projectName.value)) ||
        !isInputEdited.value
      ) {
        resetCreateProjectModal();
      } else {
        confirmModalState.value = true;
      }
    };
    const codeErr = ref({
      errMsg: '',
      onBlurState: false,
    });
    const nameErr = ref({
      errMsg: '',
      onBlurState: false,
    });
    const handleError = (input) => {
      if (input == 'projectCode') {
        codeErr.value.onBlurState = true;
        if (isProjectCodeEmpty(projectCode.value)) {
          codeErr.value.errMsg = 'This field must not be empty.';
        } else {
          codeErr.value.errMsg = '';
          codeErr.value.onBlurState = false;
        }
      } else {
        nameErr.value.onBlurState = true;
        if (isProjectNameEmpty(projectName.value)) {
          nameErr.value.errMsg = 'This field must not be empty.';
        } else {
          nameErr.value.errMsg = '';
          nameErr.value.onBlurState = false;
        }
      }
    };
    const addErrClass = (type) => {
      if (type == 'projectCode') {
        if (codeErr.value.onBlurState) return 'errClass';
        else return '';
      } else {
        if (nameErr.value.onBlurState) return 'errClass';
        else return '';
      }
    };
    const catchErrorMsg = (response) => {
      if (response.code === 422) {
        codeErr.value.errMsg = `This Project Code already exists, try another one.`;
        codeErr.value.onBlurState = true;
      } else if (response.code >= 400) {
        codeErr.value.errMsg = response.message;
        codeErr.value.onBlurState = true;
      } else {
        if (response.code) {
          codeErr.value.errMsg = response.code;
          codeErr.value.onBlurState = true;
        }
        if (response.message) {
          nameErr.value.errMsg = response.message;
          nameErr.value.onBlurState = true;
        }
      }
    };
    const handleSubmit = async () => {
      const afterResponseHandling = () => {
        toastProjectName.value = projectName.value;
        resetCreateProjectModal();
        createModalState.value = false;
        projectTagging.getProjects({});
      };
      const params = {
        Name: projectName.value,
        Code: projectCode.value,
        StatusId: projectStatus.value,
      };
      if (editModalState.value) {
        await PatchProjects(projectId.value, params).then((response) => {
          if (response.code >= 400 || response.errors) {
            catchErrorMsg(response);
          } else {
            toastEditState.value = true;
            afterResponseHandling();
          }
        });
      } else {
        try {
          await PostProjects(params);
          toastState.value = true;
          afterResponseHandling();
        } catch (err) {
          catchErrorMsg(err.response.data);
        }
      }
    };
    return {
      projectCode,
      projectName,
      projectStatus,
      handleSubmit,
      isCreateBtnDisabled,
      resetCreateProjectModal,
      closeConfirmationModal,
      confirmModalState,
      openConfirmationModal,
      handleError,
      addErrClass,
      createModalState,
      discardCreateModal,
      codeErr,
      nameErr,
      disabledClass,
      isDiscard,
      editModalState,
      submitBtnText,
      handleKeyup,
      projectStatusOptions,
      titleLabel,
      titleDescription,
    };
  },
};
</script>
<style lang="scss" scoped>
.modal-title-container {
  display: grid;
  row-gap: 8px;
  overflow-wrap: anywhere;
}
.modal-open .modal {
  background-color: rgba(0, 18, 12, 0.72) !important;
}
.modal-input-container {
  display: grid;
  row-gap: 16px;
  .modal-input {
    display: grid;
    row-gap: 16px;
    :deep(.form-outline.errClass) {
      margin-bottom: 20px;
      .form-control ~ .form-label {
        color: $red-300;
      }
      .form-control ~ .form-notch div {
        border-color: $red-300;
      }
      .form-control:focus ~ .form-label {
        color: $red-300;
      }
      .form-control:focus ~ .form-notch .form-notch-leading {
        border-top-color: $red-300;
        border-bottom-color: $red-300;
        border-left-color: $red-300;
      }
      .form-control:focus ~ .form-notch .form-notch-middle {
        border-color: $red-300;
      }
      .form-control:focus ~ .form-notch .form-notch-trailing {
        border-top-color: $red-300;
        border-bottom-color: $red-300;
        border-right-color: $red-300;
      }
    }
  }
  :deep(.select-dropdown-container) {
    .select-option:hover:not(.disabled) {
      background-color: $green-50;
    }
    .select-option.active,
    .select-option.selected.active {
      background-color: $green-75;
    }
    .select-input.focused ~ .select-arrow {
      color: $green-300;
    }
  }
  .modal-radio-container {
    display: grid;
    row-gap: 4px;
    label {
      font-size: 13px;
      line-height: 20px;
    }
    .modal-radio {
      display: flex;
      column-gap: 16px;
    }
  }
}
:deep(.btn) {
  font-size: 16px;
  box-shadow: none;
  font-weight: 500;
  padding: 4px 8px;
  &.btn-light {
    background-color: $neutral-0;
    &:hover {
      background-color: $neutral-10;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
    }
    &:active {
      background-color: $neutral-20;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
    }
  }
  &.btn-success {
    &:hover {
      background-color: $green-200;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
    }
    &:active {
      background-color: $green-400;
    }
    &.disabled {
      background-color: $neutral-40;
    }
  }
  &.btn-warning {
    background-color: $orange-300;
    &:hover {
      background-color: $orange-200;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
    }
    &:active {
      background-color: $orange-400;
    }
  }
}
</style>
